import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {Button, Size, Type} from '@Components/button';
import {ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import {useHashedId, usePosterPageHashedId} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import type {
  ClearDigitalShoAuthenticationResponse,
  ClearDigitalShoPlaylist,
  ClearDigitalShoPollingResponse,
  ClearDigitalShoUpsertInitialResponse,
  PublishingClearDigitalShoPanelProps,
} from './publishing-clear-digital-sho-panel.types';
import {ClearDigitalShoPanelProgressState} from './publishing-clear-digital-sho-panel.types';
import styles from './publishing-clear-digital-sho-panel.module.scss';
import {addClearDigitalShoOAuthBroadcastChannel, removeClearDigitalShoOAuthBroadcastChannel} from '@Libraries/clear-digital-sho-library';
import {noop} from '@Utils/general.util';
import {useAppDispatch} from '@/hooks';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useProductUsed} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';

export function PublishingClearDigitalShoPanel({...props}: PublishingClearDigitalShoPanelProps): ReactElement {
  const [selectedPlaylist, setSelectedPlaylist] = useState<ClearDigitalShoPlaylist>();
  const [playlists, setPlaylists] = useState<ClearDigitalShoPlaylist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [panelFlow, setPanelFlow] = useState<ClearDigitalShoPanelProgressState>(ClearDigitalShoPanelProgressState.STEP_CONNECT);
  const hashedId = useHashedId();
  const productUsed = useProductUsed();
  const pageHashedId = usePosterPageHashedId();
  const pageOrderId = productUsed?.existingPageOrders?.[pageHashedId] ?? productUsed?.orderId;
  const inPosterEditor = window.PMW.isReactPosterMaker();
  const dispatch = useAppDispatch();

  const unpublishFromClearDigitalSho = (): void => {
    if (props.disabled) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    window.PMW.openConfirmationModal({
      confirmButtonText: window.i18next.t('pmwjs_unlink_pos_account'),
      hideOnClose: true,
      infoText: window.i18next.t('pmwjs_unlink_x_account_confirmation', {service: 'Clear Digital Sho'}),
      panelTitle: window.i18next.t('pmwjs_confirmation'),
      onConfirm: () => {
        onUnlink();
      },
      onCancel: noop,
    });
  };

  const onUnlink = (): void => {
    if (props.disabled) {
      return;
    }
    setIsLoading(true);
    void window.PMW.writeLocal('cleardigitalsho/unlink', {})
      .then(() => {
        setIsLoading(false);
        setPanelFlow(ClearDigitalShoPanelProgressState.STEP_UNLINKED);
      })
      .catch(() => {
        showErrorPanel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const publishToClearDigitalSho = (): void => {
    if (props.disabled) {
      return;
    }

    setPanelFlow(ClearDigitalShoPanelProgressState.STEP_LOADING);
    setIsLoading(true);

    /**
     * Initial request that starts the upsert process and returns a polling key
     */
    const initiateUpsertRequest = async (): Promise<ClearDigitalShoUpsertInitialResponse> => {
      return (await window.PMW.writeLocal('cleardigitalsho/upsertDesign', {
        idPlaylist: selectedPlaylist?.id,
        idPoster: hashedId,
        idOrder: pageOrderId,
      })) as ClearDigitalShoUpsertInitialResponse;
    };

    /**
     * Polling function that checks the status of the upsert operation
     */
    const checkUpsertStatus = async (response: ClearDigitalShoUpsertInitialResponse): Promise<ClearDigitalShoPollingResponse> => {
      return window.PMW.readLocal('cleardigitalsho/checkUpsertStatus', {
        key: response.key,
      }) as Promise<ClearDigitalShoPollingResponse>;
    };

    void window.PMW.pollingAjaxCallAsync(initiateUpsertRequest, checkUpsertStatus)
      .then(() => {
        setIsLoading(false);
        setPanelFlow(ClearDigitalShoPanelProgressState.STEP_PUBLISH);
      })
      .catch(() => {
        showErrorPanel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchPlaylistsForUser = (): void => {
    if (props.disabled) {
      return;
    }

    void window.PMW.readLocal('cleardigitalsho/getUserPlaylists', {})
      .then((res: ClearDigitalShoPlaylist[]) => {
        if (res.length > 0) {
          setPlaylists(res);
        }
        setIsLoading(false);
      })
      .catch(() => {
        showErrorPanel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (props.disabled) {
      return;
    }
    const broadcastChannel = addClearDigitalShoOAuthBroadcastChannel(onOAuthSuccess, onOAuthError);

    return (): void => {
      removeClearDigitalShoOAuthBroadcastChannel(broadcastChannel);
    };
  }, []);

  const showErrorPanel = (): void => {
    dispatch(setErrorOnPublishPanel(true));
    dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
  };

  const onOAuthSuccess = (): void => {
    setPanelFlow(ClearDigitalShoPanelProgressState.STEP_AUTH);
    fetchPlaylistsForUser();
  };

  const onOAuthError = (): void => {
    showErrorPanel();
  };

  const initClearDigitalShoOAuth = (): void => {
    if (props.disabled) {
      return;
    }
    const oauthURL = window.PMW.util.site_url(`cleardigitalsho/initOAuth/${hashedId}`);
    const oauthWindow: WindowProxy | null = window.open(
      '',
      'popUpWindow',
      'height=650,width=600,left=50%,top=50%,resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no, status=yes'
    );
    setIsLoading(true);
    void window.PMW.readLocal('cleardigitalsho/checkAuthenticationStatus', {})
      .then((response): void => {
        const authResponse = response as ClearDigitalShoAuthenticationResponse;
        if (authResponse.isAuthenticated) {
          oauthWindow?.close();
          setPanelFlow(ClearDigitalShoPanelProgressState.STEP_AUTH);

          fetchPlaylistsForUser();
        } else if (oauthWindow) {
          oauthWindow.location.href = oauthURL;
        }
      })
      .catch(() => {
        showErrorPanel();
        oauthWindow?.close();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const clearDigitalShoAuthenticatedPanel = (): ReactElement => {
    return (
      <div>
        <div className={`${inPosterEditor ? styles.playlistDisplayHeightForDropDown : styles.playlistDisplayHeightForPanel} spacing-m-b-6 _overflow-auto`}>
          {playlists.map((playlist) => {
            return (
              <PublishingOptionListItem
                key={playlist.id}
                customThumbnailClasses={`${selectedPlaylist?.id === playlist.id ? 'bg-primary-10 content-primary' : 'bg-neutral-3 content-body'} `}
                iconName="icon-video-in-page"
                heading={playlist.name}
                isClicked={selectedPlaylist?.id === playlist.id}
                thumbNailType={ThumbNailType.ICON}
                onClick={() => {
                  if (props.disabled) {
                    return;
                  }
                  setSelectedPlaylist(playlist);
                }}
              />
            );
          })}
        </div>
        <div className={inPosterEditor ? 'flex-v-row' : 'flex-h-row'}>
          <Button
            onClick={publishToClearDigitalSho}
            size={inPosterEditor ? Size.SMALL : Size.MEDIUM}
            customClasses={inPosterEditor ? '' : 'flex-1 spacing-m-r-1'}
            text={window.i18next.t('pmwjs_publish')}
            type={Type.PRIMARY}
            isFullWidth={inPosterEditor}
            disabled={props.disabled}
          />
          <Button
            onClick={unpublishFromClearDigitalSho}
            customClasses={inPosterEditor ? 'spacing-m-t-2' : 'flex-1 spacing-m-l-1'}
            icon="icon-unlink"
            size={inPosterEditor ? Size.SMALL : Size.MEDIUM}
            text={window.i18next.t('pmwjs_unlink_account')}
            isFullWidth={inPosterEditor}
            type={Type.SECONDARY}
            disabled={props.disabled}
          />
        </div>
      </div>
    );
  };

  const buttonForDone = (): ReactElement => {
    return <Button isFullWidth type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_done')} onClick={props.onClose} disabled={props.disabled} />;
  };

  const getDisplayPanel = (): ReactElement => {
    if (props.disabled) {
      return (
        <DigitalSignageSubPanel
          panelHeading={'Clear Digital Sho'}
          heading={window.i18next.t('pmwjs_clear_digital_sho_signage')}
          subheading={window.i18next.t('pmwjs_clear_digital_sho_unsupported_for_videos')}
          onClose={props.onClose}
          imgSrc="publishing-clear-digital-sho.png"
        />
      );
    }

    switch (panelFlow) {
      case ClearDigitalShoPanelProgressState.STEP_CONNECT:
        return (
          <DigitalSignageSubPanel
            panelHeading={'Clear Digital Sho'}
            heading={window.i18next.t('pmwjs_clear_digital_sho_signage')}
            subheading={window.i18next.t('pmwjs_clear_digital_sho_signage_desc')}
            showButton
            buttonText={window.i18next.t('pmwjs_clear_digital_sho_connect')}
            onClick={initClearDigitalShoOAuth}
            onClose={props.onClose}
            imgSrc="publishing-clear-digital-sho.png"
          />
        );
      case ClearDigitalShoPanelProgressState.STEP_AUTH:
        return (
          <DigitalSignageSubPanel
            panelHeading={'Clear Digital Sho'}
            heading={window.i18next.t('pmwjs_clear_digital_sho_select_list')}
            subheading={window.i18next.t('pmwjs_if_no_playlist_exists')}
            listItem={clearDigitalShoAuthenticatedPanel()}
            onClose={props.onClose}
          />
        );
      case ClearDigitalShoPanelProgressState.STEP_PUBLISH:
        return (
          <DigitalSignageSubPanel
            illustration="publishing-dripboards-screens.png"
            headerClassName="flex-center"
            panelHeading={window.i18next.t('pmwjs_published')}
            heading=""
            listItem={
              <div className="flex-center flex-v-row">
                <Text
                  dangerouslySetInnerHTML
                  size={TextSize.SMALL}
                  className="content-body spacing-m-b-4"
                  val={window.i18next.t('pmwjs_clear_digital_sho_success_message', {accountUrl: 'https://sho.getcleardigital.com/playlists/my'})}
                />
                <Text size={TextSize.XSMALL} className="content-sub-text" val={window.i18next.t('pmwjs_clear_digital_sho_success_desc')} />
              </div>
            }
            showButton
            buttonComponent={buttonForDone()}
            onClose={props.onClose}
          />
        );
      case ClearDigitalShoPanelProgressState.STEP_UNLINKED:
        return (
          <DigitalSignageSubPanel
            headerClassName="flex-center"
            panelHeading={window.i18next.t('pmwjs_unlinked')}
            heading=""
            listItem={
              <div className="flex-center">
                <Text size={TextSize.SMALL} className="content-body" val={window.i18next.t('pmwjs_account_unlinked')} />
              </div>
            }
            showButton
            buttonComponent={buttonForDone()}
            onClose={props.onClose}
          />
        );
      default:
        return <div />;
    }
  };

  const getLoader = (): ReactElement => {
    return (
      <div className={`${styles.setCustomHeight} flex-v-row flex-center`}>
        <CircularProgressLoader size={LOADER_SIZE.LARGE} classes="spacing-m-0" />
        {panelFlow === ClearDigitalShoPanelProgressState.STEP_LOADING ? (
          <Text
            dangerouslySetInnerHTML
            className="content-sub-text spacing-m-t-6 spacing-p-3 -text-align-center"
            size={TextSize.SMALL}
            val={window.i18next.t('pmwjs_uploading_clear_digital_sho', {accountUrl: 'https://sho.getcleardigital.com/playlists/my'})}
          />
        ) : null}
      </div>
    );
  };

  return <>{isLoading ? getLoader() : getDisplayPanel()}</>;
}
